import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";

export interface StudioToolAction {
  id: string;
  label: string;
  hint?: string;
  // Why the pill waits, in its tooltip: a greyed pill alone does not say it.
  disabledHint?: string;
  icon: LucideIcon;
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * What the notebook offers, as pills above the composer on the right, each at hand instead of behind a "+" or loose in the
 * header (Marvin, 2026-09-29): bringing in a document, the assistant's actions (records from the notes, the
 * organizational record, the final version), then editing the text and exporting. Only deleting stays in the
 * header. The hint a menu showed under each label is the pill's tooltip. Nothing to offer, nothing shown.
 *
 * The pills come in groups, a wider gap between them: where a row is too narrow, a group moves on as a whole,
 * so no pill ends up alone on a line of its own.
 *
 * At rest the pills show their icons only; the pill the pointer comes onto, or one reached by keyboard, opens to
 * its label (Marvin, 2026-09-29), the way a card on Share opens downward, here sideways. It stays open while the
 * pointer crosses the gap to the next pill and closes only once that one opens, or once the pointer leaves the
 * row: plain :hover would close it in the gap and set the row moving under a pointer on its way over. The label
 * stays in the button throughout, so it is the button's name for a screen reader. The pointer is watched on a
 * wrapper, because a disabled button takes no pointer events: a greyed pill still opens and still says in its
 * tooltip why it waits.
 */
export function StudioToolPills({ groups, disabled }: { groups: StudioToolAction[][]; disabled: boolean }) {
  const intl = useIntl();
  const shown = groups.filter((group) => group.length);
  const [openId, setOpenId] = useState<string | null>(null);
  if (!shown.length) return null;
  return (
    <div
      role="toolbar"
      aria-label={intl.formatMessage({ id: "studio.tools", defaultMessage: "Tools" })}
      className="flex w-fit max-w-full flex-wrap gap-x-4 gap-y-2"
      onPointerLeave={() => setOpenId(null)}
    >
      {shown.map((group) => (
        <div key={group[0]!.id} className="flex flex-wrap gap-2">
          {group.map(({ id, label, hint, disabledHint, icon: Icon, disabled: unavailable, onSelect }) => (
            <span key={id} className="inline-flex" title={unavailable && disabledHint ? disabledHint : hint ?? label} onPointerEnter={() => setOpenId(id)}>
              <Button
                id={id}
                variant="chip"
                size="sm"
                disabled={disabled || unavailable}
                onClick={onSelect}
                className="group/button inline-flex items-center"
              >
                <Icon aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.6} />
                <span className={`grid transition-[grid-template-columns] duration-300 ease-out motion-reduce:transition-none ${
                  openId === id ? "grid-cols-[1fr]" : "grid-cols-[0fr] group-focus-visible/button:grid-cols-[1fr]"
                }`}>
                  <span className="overflow-hidden whitespace-nowrap">
                    <span className="pl-1.5">{label}</span>
                  </span>
                </span>
              </Button>
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
